import type { PrV2CheckSummary } from '@/lib/pr-v2/check-run-presentation';
import type { Reviewer, ReviewerStateValues } from '@/types/github';
import type { PullRequestReviewState } from '@octokit/graphql-schema';

import type { InboxSectionKey } from '../inbox-sections';
import type { InboxPrStatus, InboxPullRequest, InboxStackContext } from '../types';
import type { InboxSnoozes } from './snooze';

import { groupStackedPullRequests } from './group-stacked-pull-requests';
import { getSnoozedUntil } from './snooze';

import { getInboxCheckSummaryKey } from '@/lib/pr-v2/check-run-presentation';
import { ReviewerState } from '@/types/github';
import { buildPrPath } from '@/utils/app-paths';

/**
 * Represents stack information for a PR in the inbox
 */
export type PrStackInfo = {
  stackPosition: number;
  stackTotal: number;
  stackDepth: number;
  isPartOfStack: boolean;
  stackId?: string;
  individualStatus: InboxPrStatus;
};

/**
 * Creates a unique identifier for a PR by combining repository owner, name, and PR number.
 *
 * Why this is necessary:
 * - PR numbers are only unique within a single repository
 * - Different repositories can have PRs with the same number
 * - When calculating stack information, we need to ensure we don't mix up PRs from different repos
 * - Using a composite key (owner/repo#number) guarantees global uniqueness across all repositories
 * - This prevents bugs where PRs with the same number but from different repos would overwrite each other's stack info
 */
export function getPrUniqueId(pr: InboxPullRequest): string {
  return `${pr.repository.owner}/${pr.repository.name}#${pr.number}`;
}

/** Returns true only for PRs that have actually been merged (have a mergedAt timestamp). */
function isPullRequestMerged(pr: Pick<InboxPullRequest, 'mergedAt'>): boolean {
  return Boolean(pr.mergedAt);
}

// Utility functions
function validateReviewState(state?: PullRequestReviewState | string): ReviewerStateValues {
  if ((Object.values(ReviewerState) as string[]).includes(state as string)) {
    return state as ReviewerStateValues;
  }
  throw new Error(`Invalid review state: ${state}`);
}

export function transformReviewers(
  pr: {
    user: {
      login: string | undefined;
      avatar_url: any;
    };
    requested_reviewers: {
      login: string;
      avatar_url: string;
    }[];
  },
  reviews:
    | {
        id: string | undefined;
        user: {
          login: string | undefined;
          avatar_url: any;
        };
        state: PullRequestReviewState | undefined;
        submitted_at: any;
      }[]
    | undefined,
): Reviewer[] {
  // First, get all requested reviewers
  const requestedReviewers: Reviewer[] = (pr?.requested_reviewers || []).map((reviewer) => ({
    login: reviewer.login,
    avatarUrl: reviewer.avatar_url,
    state: ReviewerState.REQUESTED_REVIEW,
  }));

  // Then process completed reviews
  const completedReviews = (reviews || []).reduce<Reviewer[]>((acc, review) => {
    // Skip reviews without a login (e.g. deleted users or bots without a login)
    if (!review.user?.login) {
      return acc;
    }

    // Skip self-reviews by the PR author
    if (review.user.login === pr.user.login) {
      return acc;
    }

    const existingReview = acc.find((r) => r.login === review.user?.login);

    const isActionedState =
      review.state === 'APPROVED' ||
      review.state === 'CHANGES_REQUESTED' ||
      review.state === 'DISMISSED';

    if (
      !existingReview ||
      (isActionedState &&
        review.submitted_at &&
        existingReview.submitted_at &&
        new Date(review.submitted_at) > new Date(existingReview.submitted_at))
    ) {
      return acc
        .filter((r) => r.login !== review.user?.login)
        .concat({
          login: review.user.login,
          avatarUrl: review.user.avatar_url ?? '',
          state: isActionedState ? validateReviewState(review.state) : ReviewerState.COMMENTED,
          submitted_at: review.submitted_at,
          reviewId: review.id,
        });
    }
    return acc;
  }, []);

  // Strip internal fields before returning
  const normalizedCompleted: Reviewer[] = completedReviews.map(({ login, avatarUrl, state }) => ({
    login,
    avatarUrl,
    state,
  }));

  return [...requestedReviewers, ...normalizedCompleted];
}

/** A reviewer requested changes, and the author has not asked any of them to review again. */
export function isReturnedToAuthor(pr: InboxPullRequest): boolean {
  const hasChangesRequested = pr.reviewState === 'CHANGES_REQUESTED';

  // List of users who have submitted a CHANGES_REQUESTED review
  const changeRequestReviewers = (pr.reviews || [])
    .filter((rv) => rv.state === 'CHANGES_REQUESTED' && rv.user?.login)
    .map((rv) => rv.user!.login.toLowerCase());

  const reRequestedChangeReviewers = (pr.requested_reviewers || [])
    .filter(
      (reviewer) =>
        reviewer?.login && changeRequestReviewers.includes(reviewer.login.toLowerCase()),
    )
    .map((r) => r.login.toLowerCase());

  // Handle changes requested state: if the author has re-requested review from *any* of the
  // reviewers who requested changes, we're now waiting for them; otherwise it's still returned to
  // the author.
  return hasChangesRequested && reRequestedChangeReviewers.length === 0;
}

/**
 * Represents a categorized collection of pull requests organized by their review status and relationship to the current user.
 * This structure helps in displaying PRs in different sections of the inbox UI based on their current state and required actions.
 *
 * The collection holds one list per inbox section (see INBOX_SECTIONS), keyed by section.
 *
 * Additionally includes a global stack info map that contains stack information for all PRs,
 * ensuring correct stack position and total count for every member of a stack, which sits whole in one section.
 * The map uses a composite key (repo owner + repo name + PR number) to ensure uniqueness across repositories.
 */
export type GroupedPullRequests = Record<InboxSectionKey, InboxPullRequest[]> & {
  globalStackInfoMap: Map<string, PrStackInfo>;
};

/**
 * Determines which section a single pull request belongs to based on its review status, its
 * checks, and its relationship to the current user.
 *
 * The categorization follows these rules in order of precedence:
 * 1. Merged PRs -> 'merged'
 * 2. The current user's PRs: drafts -> 'drafts'; changes requested and not re-requested, or
 *    failing checks -> 'needsYou'; approved with no checks running -> 'readyToMerge'; the rest ->
 *    'waitingOnOthers'
 * 3. Other PRs: 'needsYou' when the current user, or a team they belong to, is a requested
 *    reviewer of a PR that is not back with its author, drafts included: the author asked for
 *    the review; the rest -> 'team'
 *
 * Checks come from `pr.checks`; a PR without a summary counts as passing. Snoozes are not read
 * here: groupPullRequests puts a snoozed PR in 'snoozed' before it asks.
 *
 * @param pr The pull request to categorize
 * @param currentUser GitHub username of the current user (case-insensitive comparison)
 * @returns The key of the section where this PR should be placed
 */
function categorizePullRequest(pr: InboxPullRequest, currentUser: string): InboxSectionKey {
  // Merged comes first: the optimistic merge sets mergedAt on the still-open card.
  if (isPullRequestMerged(pr)) {
    return 'merged';
  }

  // Check basic properties
  const isAuthor = pr.author && pr.author.toLowerCase() === currentUser.toLowerCase();
  const isRequestedReviewer =
    pr.requested_reviewers?.some(
      (reviewer) => reviewer?.login && reviewer.login.toLowerCase() === currentUser.toLowerCase(),
    ) || pr.requested_teams?.some((team) => team.viewer_is_member);
  const returnedToAuthor = isReturnedToAuthor(pr);

  // Author logic
  if (isAuthor) {
    if (pr.draft) {
      return 'drafts';
    }
    if (returnedToAuthor || (pr.checks?.failed ?? 0) > 0) {
      return 'needsYou';
    }
    // An approved PR still waits on checks that are running.
    const isApproved = pr.reviewState === 'APPROVED';
    return isApproved && (pr.checks?.running ?? 0) === 0 ? 'readyToMerge' : 'waitingOnOthers';
  }

  // Non-author logic (including other reviewers)
  // If the author has re-requested review from anyone who previously requested changes,
  // ALL requested reviewers should see Needs you. Active review requests beat stale approvals in
  // GitHub review history. A draft counts too: its author asked for this review early.
  if (isRequestedReviewer && !returnedToAuthor) {
    return 'needsYou';
  }

  // Non-authored PRs that are not actionable for the current user go to Team.
  return 'team';
}

/**
 * The open and merged lists can carry the same stack context; the merged list's copy wins, since
 * groupPullRequests keeps only the first context that claims a pull request.
 */
export function mergeStackContexts(
  open: readonly InboxStackContext[],
  merged: readonly InboxStackContext[],
): InboxStackContext[] {
  return Array.from(new Map([...open, ...merged].map((context) => [context.id, context])).values());
}

/** A pull request's CI summary: the batched check-summaries result, else the inbox row's own. */
export function resolveInboxCheckSummary(
  pr: InboxPullRequest,
  summaries: Readonly<Record<string, PrV2CheckSummary>>,
): PrV2CheckSummary | undefined {
  return (
    summaries[
      getInboxCheckSummaryKey({
        owner: pr.repository.owner,
        repo: pr.repository.name,
        number: pr.number,
      })
    ] ?? pr.checks
  );
}

/** What grouping reads besides the PRs and the viewer. Every caller passes each one, even empty. */
export type InboxGroupingInputs = {
  stackContexts: InboxStackContext[];
  /** The batched check summaries, by check-summary key. Checks decide a PR's section. */
  checks: Readonly<Record<string, PrV2CheckSummary>>;
  /** The viewer's snoozes. A snoozed PR sits in Snoozed until its time comes. */
  snoozes: InboxSnoozes;
};

/**
 * Groups pull requests into the inbox sections for the current user, with every PR's own status
 * in the stack info map. Stack information is not derived here: it comes exclusively from server
 * stack contexts via groupStackedPullRequests. Each PR carries its batched check summary, the one
 * the classifier reads, and so does a stack member that only its stack context brings in. A
 * snoozed PR leaves its stack first, so the rest of the stack stays one thread.
 */
export function groupPullRequests(
  prs: InboxPullRequest[],
  currentUser: string,
  { stackContexts, checks, snoozes }: InboxGroupingInputs,
): GroupedPullRequests {
  const isSnoozed = (pr: InboxPullRequest) =>
    !isPullRequestMerged(pr) && getSnoozedUntil(snoozes, pr) !== undefined;
  const withChecks = (pr: InboxPullRequest) => {
    const summary = resolveInboxCheckSummary(pr, checks);
    return summary === pr.checks ? pr : { ...pr, checks: summary };
  };

  return groupStackedPullRequests({
    // Ensure prs is an array before proceeding
    prs: (Array.isArray(prs) ? prs : []).map(withChecks),
    currentUser,
    stackContexts: stackContexts.map((context) => ({
      ...context,
      members: context.members
        .filter((member) => !isSnoozed(member.pullRequest))
        .map((member) => ({ ...member, pullRequest: withChecks(member.pullRequest) })),
    })),
    // Snoozed comes second to merged only: isSnoozed is false for a merged PR.
    categorize: (pr, user) => (isSnoozed(pr) ? 'snoozed' : categorizePullRequest(pr, user)),
    getPrId: getPrUniqueId,
  });
}

/** The app path of an inbox pull request's page. */
export function getInboxPrPath(pr: InboxPullRequest) {
  return buildPrPath({ owner: pr.repository.owner, repo: pr.repository.name, number: pr.number });
}

/**
 * Builds the ordered passive prefetch queue for Inbox V2: every PR in Needs you, then Ready to
 * merge, then Waiting on others, in their display order. Team and merged PRs are left out.
 *
 * The same PR can appear twice in the lists, so we dedupe by unique PR identity while
 * preserving the first bucket where it appeared.
 */
export function getInboxPrefetchQueue(
  groupedPRs: GroupedPullRequests | null | undefined,
  currentUser: string,
): InboxPullRequest[] {
  if (!groupedPRs || !currentUser) {
    return [];
  }

  const queue: InboxPullRequest[] = [];
  const seenPrs = new Set<string>();

  const appendUnique = (prs: InboxPullRequest[]) => {
    for (const pr of prs) {
      const prId = getPrUniqueId(pr);
      if (seenPrs.has(prId)) {
        continue;
      }

      seenPrs.add(prId);
      queue.push(pr);
    }
  };

  appendUnique(groupedPRs.needsYou);
  appendUnique(groupedPRs.readyToMerge);
  appendUnique(groupedPRs.waitingOnOthers);

  return queue;
}
